import React from 'react';
import { LayerType } from '../../types/game';
import { CharacterLayerUrls, LayerName } from '../../types/layers';
import { getLayerUrl, getLayerFallbackUrl, shouldRenderLayer, isValidUrl } from './layerMapping';

interface LayerElementOptions {
  url: string;
  layerName: LayerName;
  zIndex: number;
  displayName: string;
  renderKey?: string;
}

const createLayerElement = (options: LayerElementOptions): React.ReactElement => {
  const { url, layerName, zIndex, displayName, renderKey } = options;

  return (
    <img
      key={renderKey || layerName}
      src={url}
      alt={displayName}
      className="absolute inset-0 w-full h-full object-contain pointer-events-none"
      style={{ zIndex }}
      aria-hidden="true"
    />
  );
};

const calculateZIndex = (layerOrder: number): number => {
  return layerOrder * 10;
};

interface BuildLayerComponentsOptions {
  character: CharacterLayerUrls & { layer_armor_id?: string };
  layers: LayerType[];
  raceName?: string;
  colorName?: string;
}

export const buildLayerComponents = (
  options: BuildLayerComponentsOptions
): React.ReactElement[] => {
  const { character, layers, raceName, colorName } = options;
  const components: React.ReactElement[] = [];

  if (!layers || layers.length === 0) {
    console.warn('⚠️ [layerRenderer] No layers provided');
    return components;
  }

  console.log('🖼️ [layerRenderer] Building', layers.length, 'layer components');

  for (const layer of layers) {
    const layerName = layer.name as LayerName;

    if (!shouldRenderLayer(layerName, character)) {
      console.log('⏭️ [layerRenderer] Skipping layer:', layerName, '(condition not met)');
      continue;
    }

    let layerUrl = getLayerUrl(layerName, character);

    if (!isValidUrl(layerUrl)) {
      layerUrl = getLayerFallbackUrl(layerName, raceName, colorName);

      if (!isValidUrl(layerUrl)) {
        console.warn('⚠️ [layerRenderer] No URL for layer:', layerName);
        continue;
      }
    }

    const zIndex = calculateZIndex(layer.layer_order);

    let renderKey: string | undefined;
    if (layerName === 'layer_armor') {
      renderKey = layerUrl || 'no-armor';
    } else if (layerName === 'layer_body') {
      const shouldShow = shouldRenderLayer(layerName, character);
      renderKey = shouldShow && layerUrl ? `body-${layerUrl}` : 'body-hidden';
    }

    const element = createLayerElement({
      url: layerUrl!,
      layerName,
      zIndex,
      displayName: layer.display_name,
      renderKey
    });

    components.push(element);
  }

  console.log('✅ [layerRenderer] Rendered', components.length, 'layers');
  return components;
};

export const debugLayers = (
  character: CharacterLayerUrls & { layer_armor_id?: string },
  layers: LayerType[]
): void => {
  if (!import.meta.env.DEV) return;

  console.group('🔍 [layerRenderer] Debug Info');
  console.log('Total layers:', layers.length);

  layers.forEach(layer => {
    const layerName = layer.name as LayerName;
    const url = getLayerUrl(layerName, character);
    const shouldRender = shouldRenderLayer(layerName, character);
    const zIndex = calculateZIndex(layer.layer_order);

    console.log(`${layer.display_name} (${layerName}):`, {
      order: layer.layer_order,
      zIndex,
      shouldRender,
      hasUrl: isValidUrl(url),
      url: url || 'none'
    });
  });

  console.groupEnd();
};
